import {
  BottomSheet,
  Card,
  CbhIcon,
  Input,
  PageHeaderWrapper,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CardContent, DialogContent, FormControl, InputLabel, Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { useHistory } from "react-router-dom";

import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { LoadingButton } from "../../components/LoadingButton";
import { useInHomeVisitModalsDataContext } from "../../ShiftDiscovery/useInHomeVisitModalsDataContext";
import { VisitPricingTypes } from "../constants";
import {
  BOOKED_VISIT_LOG_CONFIRMED_MODAL_PATH,
  BOOKED_VISIT_LOG_END_TIME_MODAL_PATH,
  BOOKED_VISIT_LOG_START_TIME_MODAL_PATH,
} from "../paths";
import { useLogVisit } from "./useLogVisit";

interface LogVisitFinalizeBottomSheetProps {
  modalState: UseModalState;
  visitDate: Date;
  visitStartTime: Date;
  visitEndTime: Date | undefined;
}

/** If selecting an end time before the start time, consider it part of the next day */
const MAX_HOUR_DIFFERENCE_TO_BE_CONSIDERED_NEXT_DAY = 16;

export function LogVisitFinalizeBottomSheet(props: LogVisitFinalizeBottomSheetProps) {
  const { modalState, visitDate, visitStartTime, visitEndTime } = props;
  const { visit, generateModalPath } = useInHomeVisitModalsDataContext();
  const { showErrorToast } = useToast();
  const history = useHistory();
  const { mutateAsync: logVisit, isLoading: isLoadingLog } = useLogVisit(visit?.id ?? "");

  const { attributes: visitAttributes } = visit ?? {};
  const { pricingType } = visitAttributes ?? {};

  let estimatedDuration: number | undefined;
  if (isDefined(visitEndTime)) {
    const hourDifference = Number.parseFloat(
      ((visitEndTime.getTime() - visitStartTime.getTime()) / (1000 * 60 * 60)).toFixed(1)
    );
    estimatedDuration =
      hourDifference <= MAX_HOUR_DIFFERENCE_TO_BE_CONSIDERED_NEXT_DAY - 24
        ? 24 + hourDifference
        : hourDifference;
  }

  const completedAt = new Date(visitDate);
  completedAt.setHours(visitStartTime.getHours(), visitStartTime.getMinutes(), 0, 0);

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical" onClose={modalState.closeModal}>
          <LoadingButton
            fullWidth
            variant="contained"
            disabled={isLoadingLog || (isDefined(estimatedDuration) && estimatedDuration <= 0)}
            isLoading={isLoadingLog}
            onClick={async () => {
              if (!isDefined(visit)) {
                return;
              }

              await logVisit(
                {
                  data: {
                    type: "visitOccurrence",
                    attributes: {
                      pricingType:
                        pricingType === VisitPricingTypes.PER_HOUR
                          ? VisitPricingTypes.PER_HOUR
                          : VisitPricingTypes.PER_VISIT,
                      completedAt: completedAt.toISOString(),
                      ...(isDefined(estimatedDuration) && {
                        estimatedDuration,
                      }),
                    },
                  },
                },
                {
                  onError: () => {
                    showErrorToast("Failed to log visit. Please try again.");
                  },
                }
              );
              history.replace(
                generateModalPath(BOOKED_VISIT_LOG_CONFIRMED_MODAL_PATH, {
                  caseId: visit.attributes.caseId,
                  visitId: visit.id,
                })
              );
            }}
          >
            Log visit
          </LoadingButton>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              if (!isDefined(visit)) {
                return;
              }

              const previousModalPath = isDefined(visitEndTime)
                ? BOOKED_VISIT_LOG_END_TIME_MODAL_PATH
                : BOOKED_VISIT_LOG_START_TIME_MODAL_PATH;

              history.replace(
                generateModalPath(previousModalPath, {
                  caseId: visit.attributes.caseId,
                  visitId: visit.id,
                })
              );
            }}
          >
            Back
          </Button>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack width="100%" spacing={4} alignItems="center">
          <PageHeaderWrapper variant="tertiary" />
          <Title semibold component="h3">
            Confirm visit log
          </Title>
          <Text variant="body2">Please review the details of your visit before submitting.</Text>

          <FormControl fullWidth>
            <InputLabel>Arrival date</InputLabel>
            <Input
              disabled
              size="large"
              iconType="calendar-with-cells"
              label="Arrival date"
              value={visitDate.toLocaleDateString()}
            />
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Start time</InputLabel>
            <Input
              disabled
              size="large"
              iconType="clock"
              label="Start time"
              value={visitStartTime.toLocaleTimeString("en-US", { timeStyle: "short" })}
            />
          </FormControl>
          {isDefined(visitEndTime) && (
            <>
              <FormControl fullWidth>
                <InputLabel>End time</InputLabel>
                <Input
                  disabled
                  size="large"
                  iconType="clock"
                  label="End time"
                  value={visitEndTime.toLocaleTimeString("en-US", { timeStyle: "short" })}
                />
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Visit duration</InputLabel>
                <Input
                  disabled
                  size="large"
                  iconType="clock"
                  label="Visit duration"
                  value={`${estimatedDuration} hours`}
                />
              </FormControl>
            </>
          )}
          {isDefined(estimatedDuration) && estimatedDuration <= 0 && (
            <Card
              sx={{
                backgroundColor: (theme) => theme.palette.intent?.error.background,
                color: (theme) => theme.palette.intent?.error.text,
              }}
            >
              <CardContent>
                <Stack direction="row" spacing={2}>
                  <CbhIcon type="triangle-exclamation" size="small" />
                  <Text variant="body2">
                    Visit duration must be greater than 0 hours. Please go back and adjust your
                    times.
                  </Text>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
